import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import type { UUID } from "@/api/types";

export interface DuplicateInfo {
  exists: true;
  meeting_id: UUID;
}

export interface UploadDuplicateDialogProps {
  duplicate: DuplicateInfo;
  onOpenExisting: (meetingId: UUID) => void;
  onForceNew: () => void;
  onCancel: () => void;
}

/**
 * SHA-256 dedup gate (FR-4/5). Shown in place of the upload form once the
 * pre-check reports an existing meeting: the operator either opens the
 * existing card, deliberately uploads a copy as a new meeting, or cancels.
 */
export function UploadDuplicateDialog({
  duplicate,
  onOpenExisting,
  onForceNew,
  onCancel,
}: UploadDuplicateDialogProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-3" data-testid="upload-duplicate-dialog">
      <h4 className="text-base font-semibold">
        {t("meetings.upload.duplicate.title")}
      </h4>
      <p className="text-sm text-muted-foreground">
        {t("meetings.upload.duplicate.body")}
      </p>
      <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
        <Button variant="outline" onClick={onCancel}>
          {t("meetings.upload.duplicate.cancel")}
        </Button>
        <Button variant="outline" onClick={() => onForceNew()}>
          {t("meetings.upload.duplicate.uploadAsNew")}
        </Button>
        <Button onClick={() => onOpenExisting(duplicate.meeting_id)}>
          {t("meetings.upload.duplicate.openExisting")}
        </Button>
      </div>
    </div>
  );
}
